import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import { GoogleMap, Marker, InfoBox } from "@react-google-maps/api";

import StoreInfoCard from "../store-info-card/store-info-card";
import LocateButton from "../locate-button/locate-button";
import styles from "./map.module.scss";
import { StoreList } from "../../../types";
import { assets } from "@lululemon/dsp-shared";
import { googleMapsClient } from "@lululemon/dsp-utils";

const MAP_OPTIONS = {
  zoomControl: true,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  disableDefaultUi: false,
  maxZoom: 12,
  zoomControlOptions: {
    position: 7
  },
  styles: [
    {
      featureType: "administrative",
      elementType: "all",
      stylers: [
        {
          visibility: "on"
        },
        {
          saturation: -100
        },
        {
          lightness: 20
        }
      ]
    },
    {
      featureType: "road",
      elementType: "all",
      stylers: [
        {
          visibility: "on"
        },
        {
          saturation: -100
        },
        {
          lightness: 40
        }
      ]
    },
    {
      featureType: "water",
      elementType: "all",
      stylers: [
        {
          visibility: "on"
        },
        {
          saturation: -10
        },
        {
          lightness: 30
        }
      ]
    },
    {
      featureType: "landscape.man_made",
      elementType: "all",
      stylers: [
        {
          visibility: "simplified"
        },
        {
          saturation: -60
        },
        {
          lightness: 10
        }
      ]
    },
    {
      featureType: "landscape.natural",
      elementType: "all",
      stylers: [
        {
          visibility: "simplified"
        },
        {
          saturation: -60
        },
        {
          lightness: 60
        }
      ]
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [
        {
          visibility: "off"
        },
        {
          saturation: -100
        },
        {
          lightness: 60
        }
      ]
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [
        {
          visibility: "off"
        },
        {
          saturation: -100
        },
        {
          lightness: 60
        }
      ]
    }
  ]
};

const INFO_BOX_WIDTH = {
  mobile: 344,
  desktop: 368
};

const INITIAL_ZOOM = 3;

const Map = ({
  isMobile,
  handleOnLocateClick,
  selectThisStore,
  onStoreSelected,
  selectedPosition,
  storeList,
  infoBoxIsOpen,
  setInfoBoxIsOpen,
  publicConfig
}) => {
  const [mapInstance, setMapInstance] = React.useState(null);
  const [infoBoxPosition, setInfoBoxPosition] = useState();
  const [clickedMarker, setClickedMarker] = useState(null);

  const handleOnLoadMap = (mapInstanceParam) => {
    if (handleOnLocateClick) {
      const locateButtonDiv = document.createElement("div");
      locateButtonDiv.classList.add(styles.locateButtonWrapper);
      ReactDOM.createPortal(
        <LocateButton onClick={handleOnLocateClick} />,
        locateButtonDiv
      );
      mapInstanceParam.controls[7].push(locateButtonDiv);
      setMapInstance(mapInstanceParam);
    }
  };

  const closeInfoBox = () => {
    if (infoBoxIsOpen) {
      setInfoBoxIsOpen(false);
    }
  };

  const handleMarkerOnClick = (marker) => {
    setClickedMarker({ ...marker });
    if (infoBoxIsOpen) {
      setInfoBoxIsOpen(false);
    }
    setInfoBoxPosition({
      lat: marker.position.lat(),
      lng: marker.position.lng()
    });
    setInfoBoxIsOpen(true);
  };

  const fitStoreListBounds = () => {
    const { LatLngBounds } = googleMapsClient() || {};
    if (!LatLngBounds) return;
    const bounds = new LatLngBounds();
    // eslint-disable-next-line array-callback-return
    storeList.map((store) => {
      bounds.extend(store.position);
    });

    // automatically sets center and zoom
    mapInstance.fitBounds(bounds);
  };

  // fitBounds when storeList changes
  useEffect(() => {
    if (mapInstance)
      if (storeList.length > 0) {
        fitStoreListBounds();
      } else {
        mapInstance.panTo(selectedPosition);
        mapInstance.setZoom(12);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeList, selectedPosition]);

  if (!googleMapsClient()) return null;

  return (
    <GoogleMap
      mapContainerStyle={{ height: isMobile ? "550px" : "720px" }}
      center={selectedPosition}
      zoom={INITIAL_ZOOM}
      onLoad={handleOnLoadMap}
      options={MAP_OPTIONS}
    >
      {storeList.map((store) => {
        return (
          <Marker
            icon={assets.storeLocatorPinsUrl}
            title={store.name}
            key={`map-marker${store.name}`}
            position={store.position}
            onClick={() => handleMarkerOnClick(store)}
          />
        );
      })}
      {infoBoxIsOpen && clickedMarker && (
        <InfoBox
          position={infoBoxPosition}
          options={{
            pixelOffset: new window.google.maps.Size(
              -0.5 *
                (isMobile ? INFO_BOX_WIDTH.mobile : INFO_BOX_WIDTH.desktop),
              -55
            ),
            alignBottom: true,
            minWidth: isMobile ? INFO_BOX_WIDTH.mobile : INFO_BOX_WIDTH.desktop,
            maxWidth: isMobile ? INFO_BOX_WIDTH.mobile : INFO_BOX_WIDTH.desktop,
            boxClass: styles.infoBox,
            infoBoxClearance: isMobile
              ? new window.google.maps.Size(40, 40)
              : new window.google.maps.Size(100, 100),
            closeBoxURL: "",
            enableEventPropagation: true
          }}
        >
          <StoreInfoCard
            mapped
            distance={clickedMarker.distance}
            fullAddress={clickedMarker.fullAddress}
            hours={clickedMarker.hours}
            message={clickedMarker.message}
            specialHours={clickedMarker.specialHours}
            storeId={clickedMarker.id}
            storeName={clickedMarker.name}
            storeSlug={clickedMarker.slug}
            storeType={clickedMarker.storeType}
            closeIconClickHandler={(e) => {
              setInfoBoxIsOpen(false);
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              e.preventDefault();
              return false;
            }}
            onClickCapture={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              return false;
            }}
            showCarat
            storeStatus={clickedMarker.storeStatus}
            publicConfig={publicConfig}
            selectThisStore={selectThisStore}
            onStoreSelected={onStoreSelected}
            ctaCallback={closeInfoBox}
            storeInformationData={clickedMarker.storeInformationData}
          />
        </InfoBox>
      )}
    </GoogleMap>
  );
};

Map.propTypes = {
  isMobile: PropTypes.bool,
  handleOnLocateClick: PropTypes.func,
  storeList: StoreList.type,
  selectedPosition: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number
  }),
  onStoreSelected: PropTypes.func,
  selectThisStore: PropTypes.bool,
  infoBoxIsOpen: PropTypes.bool,
  setInfoBoxIsOpen: PropTypes.func,
  publicConfig: PropTypes.shape({
    googleMapsSearchDomain: PropTypes.string,
    googleMapsApiKey: PropTypes.string,
    nav: {
      apiHost: PropTypes.string,
      apiHostBasePath: PropTypes.string
    }
  }).isRequired
};

Map.defaultProps = {
  isMobile: false,
  handleOnLocateClick: () => {},
  storeList: [],
  selectedPosition: {},
  onStoreSelected: () => {},
  selectThisStore: false,
  infoBoxIsOpen: false,
  setInfoBoxIsOpen: () => {}
};

export default React.memo(Map);
